import React, { useState, useEffect, useCallback } from 'react';
import { logger } from '../utils/logger';
import {
  createDimension,
  updateDimension,
  getTagsByDimension,
  createTag,
  updateTag,
  deleteTag,
  getDimensionUsageStats
} from '../services/featureService';
import { notifyDimensionOrTagListChanged } from '../utils/dimensionTagSync';
import './DimensionEditor.css';

const DIMENSION_TYPES = [
  { value: 'BOOLEAN', label: 'Boolean' },
  { value: 'TRI_STATE', label: 'Yes-No' },
  { value: 'RATING', label: 'Rating' }
];

const DimensionEditor = ({ dimension, featureGroupId, onClose }) => {
  const isEditing = !!dimension;
  const [formData, setFormData] = useState({
    name: '',
    admin_directions: '',
    user_directions: '',
    dimension_type: 'BOOLEAN',
    visible_to_guest: false,
    visible_to_editor: true,
    visible_to_owner: true,
    editable_to_guest: false,
    editable_to_editor: true,
    editable_to_owner: true
  });
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});
  const [tagFormData, setTagFormData] = useState({ name: '', keywords: [] });
  const [keywordInput, setKeywordInput] = useState('');
  const [editingTag, setEditingTag] = useState(null);
  const [showTagForm, setShowTagForm] = useState(false);
  const [usageStats, setUsageStats] = useState(null);

  // Define loadTags and loadUsageStats before the useEffect that uses them
  const loadTags = useCallback(async () => {
    if (!dimension?.id) return;
    
    try {
      setLoading(true);
      const data = await getTagsByDimension(dimension.id);
      setTags(data || []);
    } catch (err) {
      logger.error('Error loading tags:', err);
      setError('Failed to load tags');
    } finally {
      setLoading(false);
    }
  }, [dimension]);

  const loadUsageStats = useCallback(async () => {
    if (!dimension?.id) return;
    
    try {
      const stats = await getDimensionUsageStats(dimension.id);
      setUsageStats(stats);
    } catch (err) {
      logger.error('Error loading usage stats:', err);
    }
  }, [dimension]);

  useEffect(() => {
    if (dimension) {
      setFormData({
        name: dimension.name || '',
        admin_directions: dimension.admin_directions || '',
        user_directions: dimension.user_directions || '',
        dimension_type: dimension.dimension_type || 'BOOLEAN',
        visible_to_guest: dimension.visible_to_guest ?? false,
        visible_to_editor: dimension.visible_to_editor ?? true,
        visible_to_owner: dimension.visible_to_owner ?? true,
        editable_to_guest: dimension.editable_to_guest ?? false,
        editable_to_editor: dimension.editable_to_editor ?? true,
        editable_to_owner: dimension.editable_to_owner ?? true
      });
      loadTags();
      loadUsageStats();
    } else {
      // Reset for new dimension
      setFormData({
        name: '',
        admin_directions: '',
        user_directions: '',
        dimension_type: 'BOOLEAN',
        visible_to_guest: false,
        visible_to_editor: true,
        visible_to_owner: true,
        editable_to_guest: false,
        editable_to_editor: true,
        editable_to_owner: true
      });
      setTags([]);
      setUsageStats(null);
    }
  }, [dimension, loadTags, loadUsageStats]);

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Dimension name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setSaving(true);
      setError(null);
      
      if (isEditing) {
        await updateDimension(dimension.id, formData);
      } else {
        await createDimension({
          ...formData,
          feature_group_id: featureGroupId
        });
      }

      notifyDimensionOrTagListChanged(featureGroupId);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save dimension');
      logger.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleAddTag = () => {
    setEditingTag(null);
    setTagFormData({ name: '', keywords: [] });
    setKeywordInput('');
    setShowTagForm(true);
  };

  const handleEditTag = (tag) => {
    setEditingTag(tag);
    setTagFormData({
      name: tag.name,
      keywords: tag.keywords || []
    });
    setKeywordInput('');
    setShowTagForm(true);
  };

  const handleDeleteTag = async (tag) => {
    if (!window.confirm(`Are you sure you want to delete the tag "${tag.name}"?`)) {
      return;
    }

    try {
      await deleteTag(tag.id);
      notifyDimensionOrTagListChanged(featureGroupId);
      await loadTags();
      await loadUsageStats();
    } catch (err) {
      alert('Failed to delete tag');
      logger.error(err);
    }
  };

  const handleTagSubmit = async (e) => {
    e.preventDefault();
    if (!tagFormData.name.trim()) {
      alert('Tag name is required');
      return;
    }

    if (!dimension?.id) {
      alert('Please save the dimension first before adding tags');
      return;
    }

    try {
      if (editingTag) {
        await updateTag(editingTag.id, tagFormData);
      } else {
        await createTag({
          dimension_id: dimension.id,
          name: tagFormData.name.trim(),
          keywords: tagFormData.keywords || []
        });
      }
      setShowTagForm(false);
      setEditingTag(null);
      setTagFormData({ name: '', keywords: [] });
      setKeywordInput('');
      notifyDimensionOrTagListChanged(featureGroupId);
      await loadTags();
      await loadUsageStats();
    } catch (err) {
      alert(editingTag ? 'Failed to update tag' : 'Failed to create tag');
      logger.error(err);
    }
  };

  const addKeyword = () => {
    const keyword = keywordInput.trim();
    if (keyword && !tagFormData.keywords.includes(keyword)) {
      setTagFormData({
        ...tagFormData,
        keywords: [...tagFormData.keywords, keyword]
      });
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword) => {
    setTagFormData({
      ...tagFormData,
      keywords: tagFormData.keywords.filter(k => k !== keyword)
    });
  };

  const canEditTags = () => {
    // Check if user can edit tags based on dimension editability
    // This should be passed from parent, but for now assume true if dimension exists
    return !!dimension?.id;
  };

  return (
    <div className={`dimension-editor ${isEditing ? 'open' : 'open'}`}>
      <div className="panel-overlay" onClick={onClose}></div>
      <div className="panel-content">
        <div className="panel-header">
          <h2>{isEditing ? 'Edit Dimension' : 'Create Dimension'}</h2>
          <button className="close-button" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="panel-body">
          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit} className="dimension-form">
            <div className="form-group">
              <label>
                Dimension Name <span className="required">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={errors.name ? 'error' : ''}
                placeholder="Enter dimension name"
                disabled={saving}
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label>Dimension Type</label>
              <select
                value={formData.dimension_type}
                onChange={(e) => setFormData({ ...formData, dimension_type: e.target.value })}
                disabled={isEditing || saving}
              >
                {DIMENSION_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
              {!isEditing && (
                <div className="dimension-type-description">
                  {formData.dimension_type === 'BOOLEAN' && (
                    <small className="help-text">
                      <strong>Boolean:</strong> This is an explicit yes/no where default is no. Good for tracking if you've reviewed all features against a criteria.
                    </small>
                  )}
                  {formData.dimension_type === 'TRI_STATE' && (
                    <small className="help-text">
                      <strong>Tri-state:</strong> This is a yes/no case where you can have "null" for cases you haven't evaluated yet.
                    </small>
                  )}
                  {formData.dimension_type === 'RATING' && (
                    <small className="help-text">
                      <strong>Ratings:</strong> This is a 1-5 value with a null option.
                    </small>
                  )}
                </div>
              )}
              {isEditing && (
                <small className="help-text">
                  Dimension type cannot be changed after creation. Changes could result in data loss.
                </small>
              )}
            </div>

            <div className="form-group">
              <label>Admin Directions</label>
              <textarea
                value={formData.admin_directions}
                onChange={(e) => setFormData({ ...formData, admin_directions: e.target.value })}
                placeholder="Optional guidance for admins who may want to add this dimension to their Feature Map"
                rows={3}
                disabled={saving}
              />
              <small className="help-text">
                Shown to admins when managing dimensions
              </small>
            </div>

            <div className="form-group">
              <label>User Directions</label>
              <textarea
                value={formData.user_directions}
                onChange={(e) => setFormData({ ...formData, user_directions: e.target.value })}
                placeholder="Optional guidance for users when tagging features"
                rows={3}
                disabled={saving}
              />
              <small className="help-text">
                Shown to users when tagging features with this dimension
              </small>
            </div>

            <div className="form-section">
              <h3>Visibility Permissions</h3>
              <div className="permission-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.visible_to_guest}
                    onChange={(e) => setFormData({ ...formData, visible_to_guest: e.target.checked })}
                    disabled={saving}
                  />
                  Visible to Guests
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.visible_to_editor}
                    onChange={(e) => setFormData({ ...formData, visible_to_editor: e.target.checked })}
                    disabled={saving}
                  />
                  Visible to Editors
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.visible_to_owner}
                    onChange={(e) => setFormData({ ...formData, visible_to_owner: e.target.checked })}
                    disabled={saving}
                  />
                  Visible to Owners
                </label>
              </div>
            </div>

            <div className="form-section">
              <h3>Editability Permissions</h3>
              <div className="permission-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.editable_to_guest}
                    onChange={(e) => setFormData({ ...formData, editable_to_guest: e.target.checked })}
                    disabled={saving}
                  />
                  Editable by Guests
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.editable_to_editor}
                    onChange={(e) => setFormData({ ...formData, editable_to_editor: e.target.checked })}
                    disabled={saving}
                  />
                  Editable by Editors
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.editable_to_owner}
                    onChange={(e) => setFormData({ ...formData, editable_to_owner: e.target.checked })}
                    disabled={saving}
                  />
                  Editable by Owners
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button type="button" onClick={onClose} className="cancel-button" disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="save-button" disabled={saving}>
                {saving ? 'Saving...' : (isEditing ? 'Update' : 'Create')} Dimension
              </button>
            </div>
          </form>

          {isEditing && dimension?.id && (
            <div className="tags-section">
              <div className="tags-section-header">
                <h3>Tags</h3>
                {usageStats && (
                  <div className="usage-stats">
                    {usageStats.percentage}% of features have tags ({usageStats.features_with_tags} of {usageStats.total_features})
                  </div>
                )}
                {canEditTags() && (
                  <button className="add-tag-button" onClick={handleAddTag}>
                    <i className="fas fa-plus"></i> Add Tag
                  </button>
                )}
              </div>

              {showTagForm && (
                <form onSubmit={handleTagSubmit} className="tag-form">
                  <div className="form-group">
                    <label>
                      Tag Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={tagFormData.name}
                      onChange={(e) => setTagFormData({ ...tagFormData, name: e.target.value })}
                      placeholder="Enter tag name"
                    />
                  </div>

                  <div className="form-group">
                    <label>Keywords</label>
                    <div className="keywords-input">
                      <input
                        type="text"
                        value={keywordInput}
                        onChange={(e) => setKeywordInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addKeyword();
                          }
                        }}
                        placeholder="Enter keyword and press Enter"
                      />
                      <button type="button" onClick={addKeyword} className="add-keyword-button">
                        <i className="fas fa-plus"></i>
                      </button>
                    </div>
                    {tagFormData.keywords.length > 0 && (
                      <div className="keywords-list">
                        {tagFormData.keywords.map((keyword, index) => (
                          <span key={index} className="keyword-tag">
                            {keyword}
                            <button
                              type="button"
                              onClick={() => removeKeyword(keyword)}
                              className="remove-keyword"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="form-actions">
                    <button type="button" onClick={() => {
                      setShowTagForm(false);
                      setEditingTag(null);
                      setTagFormData({ name: '', keywords: [] });
                    }} className="cancel-button">
                      Cancel
                    </button>
                    <button type="submit" className="save-button">
                      {editingTag ? 'Update' : 'Create'} Tag
                    </button>
                  </div>
                </form>
              )}

              {loading ? (
                <div className="loading">Loading tags...</div>
              ) : (
                <div className="tags-list">
                  {tags.length === 0 ? (
                    <div className="no-tags">No tags yet. Click "Add Tag" to create one.</div>
                  ) : (
                    <table className="tags-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Keywords</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tags.map(tag => (
                          <tr key={tag.id}>
                            <td>{tag.name}</td>
                            <td>
                              {tag.keywords && tag.keywords.length > 0 ? (
                                <div className="keywords-display">
                                  {tag.keywords.slice(0, 3).map((keyword, index) => (
                                    <span key={index} className="keyword-chip">{keyword}</span>
                                  ))}
                                  {tag.keywords.length > 3 && (
                                    <span className="keyword-more">+{tag.keywords.length - 3} more</span>
                                  )}
                                </div>
                              ) : (
                                <span className="no-keywords">None</span>
                              )}
                            </td>
                            <td className="tag-actions">
                              {canEditTags() && (
                                <>
                                  <button
                                    className="action-button"
                                    onClick={() => handleEditTag(tag)}
                                    title="Edit tag"
                                  >
                                    <i className="fas fa-edit"></i>
                                  </button>
                                  <button
                                    className="action-button delete"
                                    onClick={() => handleDeleteTag(tag)}
                                    title="Delete tag"
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DimensionEditor;


